import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from './services/firebase';
import { MasterOutput } from './components/WaveLab/MasterOutput';
import { WaveCard } from './components/WaveLab/WaveCard';
import { FourierLab, FourierLabHandle } from './components/Fourier/FourierLab';
import WavePool from './components/WavePool/WavePool';
import WavePoolControls from './components/WavePool/Controls';
import ConfigEditor from './components/WavePool/ConfigEditor';
import AIPanel from './components/WavePool/AIPanel';
import { WavePoolConfigManager } from './components/WavePool/WavePoolConfigManager';
import { SimulationConfig, WavePattern, Emitter, Point, AIWaveResponse } from './components/WavePool/types';
import { DEFAULT_CONFIG } from './components/WavePool/constants';
import { AuthPanel } from './components/auth/AuthPanel';
import { UserSettingsModal } from './components/auth/UserSettingsModal';
import { ConfigManager } from './components/config/ConfigManager';
import { AnalysisPanel } from './components/analysis/AnalysisPanel';
import { AudioProcessor } from './components/audio/AudioProcessor';
import { AudioUploader } from './components/audio/AudioUploader';
import { MusicPanel } from './components/audio/MusicPanel';
import { MusicLibraryMode } from './components/audio/MusicLibraryMode';
import { MusicPlayer } from './components/audio/MusicPlayer';
import { QueuePanel } from './components/audio/QueuePanel';
import { TermsOfUse } from './components/legal/TermsOfUse';
import { ThemeProvider } from './components/ui/ThemeContext';
import { Wave, COLORS, FavoriteFrequency, Song, Playlist } from './types';
import { UserProfile } from './services/authService';
import analyticsService from './services/analyticsService';
import configService from './services/configService';
import musicService from './services/musicService';
import themeService from './services/themeService';
import audioAnalysisService from './services/audioAnalysisService';
// Import analytics debugger in development
if (import.meta.env.DEV) {
  import('./services/analyticsDebugger');
}

// localStorage keys for music player settings
const MUSIC_SETTINGS_STORAGE_KEY = 'resynthLab_musicSettings';

const normalizeWavePoolConfig = (config: SimulationConfig): SimulationConfig => ({
  ...DEFAULT_CONFIG,
  ...config,
  baseFrequency: config.baseFrequency ?? config.frequency ?? DEFAULT_CONFIG.baseFrequency,
});

// Load music settings from localStorage
const loadMusicSettings = () => {
  try {
    const data = localStorage.getItem(MUSIC_SETTINGS_STORAGE_KEY);
    if (data) {
      const settings = JSON.parse(data);
      // Deduplicate queues by song ID
      if (settings.playQueue) {
        const seen = new Set();
        settings.playQueue = settings.playQueue.filter((song: Song) => {
          if (seen.has(song.id)) {
            console.warn('Removed duplicate song from playQueue:', song.id);
            return false;
          }
          seen.add(song.id);
          return true;
        });
      }
      if (settings.originalQueue) {
        const seen = new Set();
        settings.originalQueue = settings.originalQueue.filter((song: Song) => {
          if (seen.has(song.id)) {
            console.warn('Removed duplicate song from originalQueue:', song.id);
            return false;
          }
          seen.add(song.id);
          return true;
        });
      }
      return settings;
    }
  } catch (error) {
    console.error('Failed to load music settings from localStorage:', error);
  }
  return {
    volume: 0.8,
    loopMode: 'none',
    shuffleEnabled: false,
    playQueue: [],
    originalQueue: [],
    currentSongId: null
  };
};

// Save music settings to localStorage
const saveMusicSettings = (settings: { 
  volume: number; 
  loopMode: 'none' | 'one' | 'all'; 
  shuffleEnabled: boolean;
  playQueue: Song[];
  originalQueue: Song[];
  currentSongId: string | null;
}) => {
  try {
    localStorage.setItem(MUSIC_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save music settings to localStorage:', error);
  }
};

function App() {
  const [activeTab, setActiveTab] = useState<'interference' | 'fourier' | 'music' | 'wavepool'>('interference');
  const [nextId, setNextId] = useState(6);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [importedWavesToFourier, setImportedWavesToFourier] = useState<Wave[] | null>(null);
  const [showAudioProcessor, setShowAudioProcessor] = useState(false);
  const [showAudioUploader, setShowAudioUploader] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [uploadedAudioData, setUploadedAudioData] = useState<Float32Array | null>(null);
  const [waveformData, setWaveformData] = useState<{ left: Float32Array<ArrayBuffer>; right: Float32Array<ArrayBuffer> } | null>(null);
  const [isUploadedAudioPlaying, setIsUploadedAudioPlaying] = useState(false);
  const [isUploadedAudioPaused, setIsUploadedAudioPaused] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [seekTrigger, setSeekTrigger] = useState(0); // Triggers time update restart after seek
  const [showRawFrequencies, setShowRawFrequencies] = useState<boolean>(false); // Toggle between raw and filtered frequencies
  
  // Initialize music player settings from localStorage
  const initialMusicSettings = loadMusicSettings();
  const [playbackVolume, setPlaybackVolume] = useState(initialMusicSettings.volume);
  const [loopMode, setLoopMode] = useState<'none' | 'one' | 'all'>(initialMusicSettings.loopMode);
  const [shuffleEnabled, setShuffleEnabled] = useState(initialMusicSettings.shuffleEnabled);
  const [playQueue, setPlayQueue] = useState<Song[]>(initialMusicSettings.playQueue || []);
  const [originalQueue, setOriginalQueue] = useState<Song[]>(initialMusicSettings.originalQueue || []);
  const [songs, setSongs] = useState<Song[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [currentSongId, setCurrentSongId] = useState<string | null>(initialMusicSettings.currentSongId || null);
  const [isLoadingSong, setIsLoadingSong] = useState(false);
  const [showMusicPlayer, setShowMusicPlayer] = useState(() => {
    const saved = localStorage.getItem('showMusicPlayer');
    return saved ? JSON.parse(saved) : false;
  });
  const [showQueuePanel, setShowQueuePanel] = useState(() => {
    const saved = localStorage.getItem('showQueuePanel');
    return saved ? JSON.parse(saved) : false;
  });
  const [isMusicExpanded, setIsMusicExpanded] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(() => {
    const saved = localStorage.getItem('isPanelOpen');
    return saved ? JSON.parse(saved) : false;
  });
  const [isPanelRightOpen, setIsPanelRightOpen] = useState(() => {
    const saved = localStorage.getItem('isPanelRightOpen');
    return saved ? JSON.parse(saved) : false;
  });
  const [showTerms, setShowTerms] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [isLayersExpanded, setIsLayersExpanded] = useState(true);
  const [isVisualizerFullBrowser, setIsVisualizerFullBrowser] = useState(false);
  const [isPresetsExpanded, setIsPresetsExpanded] = useState(false);
  const [isSavedConfigsExpanded, setIsSavedConfigsExpanded] = useState(false);
  const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isWavePoolSavedConfigsExpanded, setIsWavePoolSavedConfigsExpanded] = useState(false);
  const [isWavePoolPresetsExpanded, setIsWavePoolPresetsExpanded] = useState(false);
  const [showWavePoolSaveDialog, setShowWavePoolSaveDialog] = useState(false);
  const [wavePoolConfigRefreshTrigger, setWavePoolConfigRefreshTrigger] = useState(0);
  
  // Default configuration based on "Stereo Prism" preset
  const defaultWavesInit: Wave[] = [
    { id: 1, freq: 4, amp: 30, phase: 0, color: COLORS[0], muted: false, channel: 'left', waveform: 'sine' },
    { id: 2, freq: 5, amp: 35, phase: 0, color: COLORS[1], muted: false, channel: 'left', waveform: 'sine' },
    { id: 3, freq: 6, amp: 40, phase: 0, color: COLORS[2], muted: false, channel: 'both', waveform: 'sine' },
    { id: 4, freq: 7, amp: 35, phase: 0, color: COLORS[3], muted: false, channel: 'right', waveform: 'sine' },
    { id: 5, freq: 8, amp: 30, phase: 0, color: COLORS[4], muted: false, channel: 'right', waveform: 'sine' }
  ];

  const [waves, setWaves] = useState<Wave[]>(() => {
    const saved = localStorage.getItem('frequencyLayers');
    return saved ? JSON.parse(saved) : defaultWavesInit;
  });
  const [speed, setSpeed] = useState(() => {
    const saved = localStorage.getItem('waveLab_speed');
    return saved ? JSON.parse(saved) : 1;
  });

  // Wave Pool state
  const [wavePoolConfig, setWavePoolConfig] = useState<SimulationConfig>(normalizeWavePoolConfig(DEFAULT_CONFIG));
  const [wavePoolEmitters, setWavePoolEmitters] = useState<Emitter[]>([]);
  const [wavePoolFocusPoints, setWavePoolFocusPoints] = useState<Point[] | undefined>([
    { x: 0.5, y: 0.3 }, { x: 0.48, y: 0.33 }, { x: 0.46, y: 0.36 }, { x: 0.44, y: 0.39 }, { x: 0.42, y: 0.42 },
    { x: 0.4, y: 0.45 }, { x: 0.38, y: 0.48 }, { x: 0.36, y: 0.51 }, { x: 0.34, y: 0.54 }, { x: 0.32, y: 0.57 },
    { x: 0.32, y: 0.6 }, { x: 0.35, y: 0.6 }, { x: 0.38, y: 0.6 }, { x: 0.41, y: 0.6 }, { x: 0.44, y: 0.6 },
    { x: 0.47, y: 0.6 }, { x: 0.5, y: 0.6 }, { x: 0.53, y: 0.6 }, { x: 0.56, y: 0.6 }, { x: 0.59, y: 0.6 },
    { x: 0.62, y: 0.6 }, { x: 0.65, y: 0.6 }, { x: 0.68, y: 0.6 }, { x: 0.68, y: 0.6 }, { x: 0.66, y: 0.57 },
    { x: 0.64, y: 0.54 }, { x: 0.62, y: 0.51 }, { x: 0.6, y: 0.48 }, { x: 0.58, y: 0.45 }, { x: 0.56, y: 0.42 },
    { x: 0.54, y: 0.39 }, { x: 0.52, y: 0.36 }, { x: 0.5, y: 0.33 }, { x: 0.5, y: 0.7 }, { x: 0.48, y: 0.67 },
    { x: 0.46, y: 0.64 }, { x: 0.44, y: 0.61 }, { x: 0.42, y: 0.58 }, { x: 0.4, y: 0.55 }, { x: 0.38, y: 0.52 },
    { x: 0.36, y: 0.49 }, { x: 0.34, y: 0.46 }, { x: 0.32, y: 0.43 }, { x: 0.32, y: 0.4 }, { x: 0.35, y: 0.4 },
    { x: 0.38, y: 0.4 }, { x: 0.41, y: 0.4 }, { x: 0.44, y: 0.4 }, { x: 0.47, y: 0.4 }, { x: 0.5, y: 0.4 },
    { x: 0.53, y: 0.4 }, { x: 0.56, y: 0.4 }, { x: 0.59, y: 0.4 }, { x: 0.62, y: 0.4 }, { x: 0.65, y: 0.4 },
    { x: 0.68, y: 0.4 }, { x: 0.68, y: 0.4 }, { x: 0.66, y: 0.43 }, { x: 0.64, y: 0.46 }, { x: 0.62, y: 0.49 },
    { x: 0.6, y: 0.52 }, { x: 0.58, y: 0.55 }, { x: 0.56, y: 0.58 }, { x: 0.54, y: 0.61 }, { x: 0.52, y: 0.64 },
    { x: 0.5, y: 0.67 }
  ]);
  const [wavePoolResetTrigger, setWavePoolResetTrigger] = useState(0);
  const [wavePoolAIResponse, setWavePoolAIResponse] = useState<AIWaveResponse>({
    name: 'Solfeggio Frequency: 852Hz',
    description: 'Sacred frequency associated with cellular repair, intuition, and spiritual awakening.',
    config: DEFAULT_CONFIG,
    emitters: [],
    focusPoints: wavePoolFocusPoints as Point[]
  });

  // Keep wavesRef in sync with waves state
  useEffect(() => {
    wavesRef.current = waves;
  }, [waves]);

  // Save frequency layers to localStorage when they change
  useEffect(() => {
    localStorage.setItem('frequencyLayers', JSON.stringify(waves));
  }, [waves]);

  // Save speed to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('waveLab_speed', JSON.stringify(speed));
  }, [speed]);

  // Save panel open/closed states to localStorage
  useEffect(() => {
    localStorage.setItem('isPanelOpen', JSON.stringify(isPanelOpen));
  }, [isPanelOpen]);

  useEffect(() => {
    localStorage.setItem('isPanelRightOpen', JSON.stringify(isPanelRightOpen));
  }, [isPanelRightOpen]);

  // Save music player and queue panel open/closed states to localStorage
  useEffect(() => {
    localStorage.setItem('showMusicPlayer', JSON.stringify(showMusicPlayer));
  }, [showMusicPlayer]);

  useEffect(() => {
    localStorage.setItem('showQueuePanel', JSON.stringify(showQueuePanel));
  }, [showQueuePanel]);

  // Audio playback refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const fourierLabRef = useRef<FourierLabHandle>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserLeftRef = useRef<AnalyserNode | null>(null);
  const analyserRightRef = useRef<AnalyserNode | null>(null);
  const splitterRef = useRef<ChannelSplitterNode | null>(null);
  const mergerRef = useRef<ChannelMergerNode | null>(null);
  const isStereoRef = useRef<boolean>(false);
  const gainNodeRef = useRef<GainNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isPausedRef = useRef<boolean>(false);
  const playbackStartTimeRef = useRef<number>(0);
  const pausedAtTimeRef = useRef<number>(0);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const lastPeakWarningRef = useRef<number>(0);
  const lastFrequencyDataRef = useRef<Float32Array | null>(null);
  const handleSongEndedRef = useRef<() => void>(() => {});
  const handleSeekRef = useRef<(time: number) => Promise<void>>(async () => {});
  const userRef = useRef<User | null>(null);
  const wavesRef = useRef<Wave[]>(waves);
  const isAnalyzingRef = useRef<boolean>(false);

  // Audio buffer pooling to reduce GC pressure
  const frequencyDataPoolRef = useRef<{ left: Uint8Array<ArrayBuffer>; right: Uint8Array<ArrayBuffer> } | null>(null);
  const timeDomainPoolRef = useRef<{ left: Float32Array<ArrayBuffer>; right: Float32Array<ArrayBuffer> } | null>(null);
  const waveformDataPoolRef = useRef<{ left: Float32Array<ArrayBuffer>; right: Float32Array<ArrayBuffer> } | null>(null);
  const lastStateUpdateTimeRef = useRef<number>(0);

  // Track initial page view and tab changes
  useEffect(() => {
    analyticsService.trackPageView('FrequencyCanvas App');
  }, []);

  // Cleanup audio resources on component unmount
  useEffect(() => {
    return () => {
      // Cancel any pending animation frames
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Stop and cleanup source node
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.onended = null;
          sourceNodeRef.current.stop();
          sourceNodeRef.current.disconnect();
        } catch (e) {}
        sourceNodeRef.current = null;
      }

      // Disconnect and cleanup all audio nodes
      [analyserRef, analyserLeftRef, analyserRightRef, splitterRef, mergerRef, gainNodeRef].forEach(ref => {
        if (ref.current) {
          try {
            ref.current.disconnect();
          } catch (e) {}
          ref.current = null;
        }
      });

      // Close audio context if open
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }

      // Clear buffers
      audioBufferRef.current = null;
      lastFrequencyDataRef.current = null;
    };
  }, []);

  // Sync theme from user profile
  useEffect(() => {
    if (userProfile?.preferences?.theme) {
      themeService.syncFromUserProfile(userProfile.preferences.theme);
    }
  }, [userProfile]);

  // Auto-enable Raw mode when music starts playing
  useEffect(() => {
    if (isUploadedAudioPlaying && !showRawFrequencies) {
      setShowRawFrequencies(true);
    }
  }, [isUploadedAudioPlaying]);

  // Define handleLoadConfig before it's used in effects
  const handleLoadConfig = useCallback((newWaves: Wave[], newSpeed?: number) => {
    // Update IDs to avoid conflicts using functional updates
    setNextId(currentNextId => {
      const updatedWaves = newWaves.map((wave, index) => ({
        ...wave,
        id: currentNextId + index,
        waveform: wave.waveform || 'sine'
      }));
      
      setWaves(updatedWaves);
      return currentNextId + updatedWaves.length;
    });
    
    // Update speed if provided
    if (newSpeed !== undefined) {
      setSpeed(newSpeed);
    }
  }, []);

  // Load shared preset from URL parameter
  useEffect(() => {
    const loadSharedPreset = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedId = urlParams.get('shared');
      
      if (sharedId) {
        try {
          const sharedConfig = await configService.loadSharedConfig(sharedId);
          
          if (sharedConfig && sharedConfig.waves) {
            // Load the shared configuration
            handleLoadConfig(sharedConfig.waves);
            
            // Track shared preset load
            analyticsService.trackFeatureUse('shared_preset_load', `shared_id:${sharedId}`);
            
            // Optionally show a notification
            console.log(`Loaded shared preset: ${sharedConfig.name}`);
            
            // Keep the query parameter in URL to ensure config persists across auth state changes
            // Users can manually clear it if desired, or it will be removed on navigation
          }
        } catch (error) {
          console.error('Failed to load shared preset:', error);
          alert('Failed to load shared preset. The link may be invalid or expired.');
          // Remove invalid shared parameter from URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };
    
    loadSharedPreset();
  }, [handleLoadConfig]); // Include handleLoadConfig in dependencies

  useEffect(() => {
    const getTabName = () => {
      switch (activeTab) {
        case 'interference': return 'Wave Lab';
        case 'fourier': return 'Fourier Lab';
        case 'music': return 'Music Library';
        case 'wavepool': return 'Wave Pool';
        default: return 'Unknown';
      }
    };
    analyticsService.trackPageView(getTabName());
    analyticsService.trackFeatureUse('tab_change', activeTab);
  }, [activeTab]);

  // Wave Pool pattern logic
  useEffect(() => {
    if (
      wavePoolConfig.pattern !== WavePattern.AI_GENERATED &&
      wavePoolConfig.pattern !== WavePattern.PRESET &&
      wavePoolFocusPoints
    ) {
      setWavePoolFocusPoints(undefined);
      setWavePoolAIResponse(null);
    }

    switch (wavePoolConfig.pattern) {
      case WavePattern.MANUAL:
        setWavePoolEmitters([]);
        break;
      case WavePattern.SINGLE_DROP:
        setWavePoolEmitters([
          { x: 0.5, y: 0.5, frequency: 1, phase: 0, active: true }
        ]);
        break;
      case WavePattern.DOUBLE_SLIT:
        setWavePoolEmitters([
          { x: 0.4, y: 0.5, frequency: 1, phase: 0, active: true },
          { x: 0.6, y: 0.5, frequency: 1, phase: 0, active: true }
        ]);
        break;
      case WavePattern.CONCENTRIC:
        setWavePoolEmitters([
          { x: 0.5, y: 0.3, frequency: 0.5, phase: 0, active: true },
          { x: 0.35, y: 0.7, frequency: 0.5, phase: 0, active: true },
          { x: 0.65, y: 0.7, frequency: 0.5, phase: 0, active: true }
        ]);
        break;
      case WavePattern.CHAOS:
        const chaos: Emitter[] = [];
        for(let i = 0; i < 5; i++) {
          chaos.push({
            x: Math.random() * 0.8 + 0.1,
            y: Math.random() * 0.8 + 0.1,
            frequency: Math.random() + 0.5,
            phase: Math.random() * Math.PI,
            active: true
          });
        }
        setWavePoolEmitters(chaos);
        break;
      case WavePattern.PRESET:
        // Presets carry their own emitters/focus points
        break;
      case WavePattern.AI_GENERATED:
        // Handled by AI callback
        break;
    }
  }, [wavePoolConfig.pattern]);

  // Handle AI Wave Pattern Responses
  const handleAIWaveResponse = useCallback((response: AIWaveResponse) => {
    setWavePoolAIResponse(response);
    // Apply the AI-generated configuration
    if (response.config) {
      setWavePoolConfig(prev => normalizeWavePoolConfig({ ...prev, ...response.config }));
    }
    if (response.emitters) {
      setWavePoolEmitters(response.emitters);
    }
    if (response.focusPoints) {
      setWavePoolFocusPoints(response.focusPoints);
    }
    // Set pattern to AI_GENERATED
    setWavePoolConfig(prev => ({ ...prev, pattern: WavePattern.AI_GENERATED }));
  }, []);

  // Persist music player settings to localStorage
  useEffect(() => {
    saveMusicSettings({
      volume: playbackVolume,
      loopMode,
      shuffleEnabled,
      playQueue,
      originalQueue,
      currentSongId
    });
  }, [playbackVolume, loopMode, shuffleEnabled, playQueue, originalQueue, currentSongId]);

  const addWave = (partial?: Partial<Wave>) => {
    const color = COLORS[(nextId - 1) % COLORS.length];
    setWaves(prev => [
      ...prev, 
      { id: nextId, freq: 220, amp: 50, phase: 0, color, muted: false, channel: 'both', waveform: 'sine', ...partial }
    ]);
    setNextId(n => n + 1);
    
    // Track wave creation
    analyticsService.trackFeatureUse('wave_create', `total_waves:${waves.length + 1}`);
  };

  const resetWaves = () => {
    setWaves(defaultWavesInit);
    setSpeed(1);
    setNextId(6);
    analyticsService.trackFeatureUse('wave_reset', 'reset_to_defaults');
  };

  const mergeAllWaves = () => {
    // Filter out muted waves
    const activeWaves = waves.filter(w => !w.muted && w.amp > 0);
    
    if (activeWaves.length === 0) {
      alert('No active waves to merge. Please ensure at least one wave is unmuted with amplitude > 0.');
      return;
    }
    
    if (activeWaves.length === 1) {
      alert('Only one active wave found. Nothing to merge.');
      return;
    }
    
    // Calculate weighted average frequency based on amplitude (energy-weighted)
    let totalWeightedFreq = 0;
    let totalEnergy = 0;
    
    activeWaves.forEach(wave => {
      const energy = wave.amp * wave.amp; // Energy proportional to amplitude squared
      totalWeightedFreq += wave.freq * energy;
      totalEnergy += energy;
    });
    
    const mergedFreq = Math.round(totalWeightedFreq / totalEnergy);
    
    // Use RMS (Root Mean Square) for amplitude - more physically accurate
    // RMS = sqrt(mean(amplitudes^2))
    const sumSquares = activeWaves.reduce((sum, wave) => sum + (wave.amp * wave.amp), 0);
    const mergedAmp = Math.min(100, Math.round(Math.sqrt(sumSquares / activeWaves.length)));
    
    // For phase and color, use the dominant (highest amplitude) wave
    // since phase relationships in superposition are complex
    const dominantWave = activeWaves.reduce((max, wave) => 
      wave.amp > max.amp ? wave : max, activeWaves[0]);
    const mergedPhase = dominantWave.phase;
    
    // Create merged wave
    const mergedWave: Wave = {
      id: nextId,
      freq: mergedFreq,
      amp: mergedAmp,
      phase: mergedPhase,
      color: dominantWave.color,
      muted: false,
      channel: 'both',
      waveform: dominantWave.waveform || 'sine'
    };
    
    // Replace all waves with the merged one
    setWaves([mergedWave]);
    setNextId(n => n + 1);
    
    analyticsService.trackFeatureUse('wave_merge', `merged_count:${activeWaves.length}`);
  };

  const removeWave = (id: number) => {
    setWaves(prev => prev.filter(w => w.id !== id));
    analyticsService.trackFeatureUse('wave_remove', `remaining_waves:${waves.length - 1}`);
  };

  const updateWave = (id: number, updates: Partial<Wave>) => {
    setWaves(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w));
    
    // Track specific wave parameter changes
    if (updates.freq !== undefined) {
      analyticsService.trackFeatureUse('wave_frequency_change', `new_freq:${updates.freq}`);
    }
    if (updates.amp !== undefined) {
      analyticsService.trackFeatureUse('wave_amplitude_change', `new_amp:${updates.amp}`);
    }
    if (updates.phase !== undefined) {
      analyticsService.trackFeatureUse('wave_phase_change', `new_phase:${updates.phase}`);
    }
    if (updates.muted !== undefined) {
      analyticsService.trackFeatureUse('wave_mute_toggle', `muted:${updates.muted}`);
    }
  };

  const handleQuickSave = async () => {
    if (!user) {
      alert('Please sign in to save configurations');
      return;
    }
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
      await configService.saveConfig({
        name: `Quick Save ${timestamp}`,
        description: 'Auto-saved configuration',
        waves,
        speed,
        tags: ['auto-save'],
        isPublic: false
      });
      alert('Configuration saved!');
    } catch (error) {
      console.error('Failed to quick save:', error);
      alert('Failed to save configuration. Please try again.');
    }
  };

  // Wave Pool configuration handlers
  const handleWavePoolLoadConfig = async (configId: string) => {
    try {
      const config = await configService.loadWavePoolConfig(configId);
      setWavePoolConfig(normalizeWavePoolConfig(config.poolConfig));
      setWavePoolEmitters(config.emitters);
      if (config.focusPoints) {
        setWavePoolFocusPoints(config.focusPoints);
      }
      analyticsService.trackFeatureUse('wavepool_config_load', `config_id:${configId}`);
    } catch (error) {
      console.error('Failed to load Wave Pool config:', error);
      alert('Failed to load configuration. Please try again.');
      analyticsService.trackError('wavepool_config_load_failed', String(error));
    }
  };

  const handleWavePoolDeleteConfig = (configId: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;
    
    try {
      configService.deleteWavePoolConfig(configId);
      analyticsService.trackFeatureUse('wavepool_config_delete');
    } catch (error) {
      console.error('Failed to delete Wave Pool config:', error);
      alert('Failed to delete configuration. Please try again.');
    }
  };

  const handleStopSong = useCallback((clearQueue: boolean = false) => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.onended = null; // Clear handler to prevent triggering auto-play
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      } catch (e) {
        // Already stopped
      }
      sourceNodeRef.current = null;
    }

    // Clean up all analyser nodes
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {}
      analyserRef.current = null;
    }

    if (analyserLeftRef.current) {
      try {
        analyserLeftRef.current.disconnect();
      } catch (e) {}
      analyserLeftRef.current = null;
    }

    if (analyserRightRef.current) {
      try {
        analyserRightRef.current.disconnect();
      } catch (e) {}
      analyserRightRef.current = null;
    }

    // Clean up channel splitter and merger
    if (splitterRef.current) {
      try {
        splitterRef.current.disconnect();
      } catch (e) {}
      splitterRef.current = null;
    }

    if (mergerRef.current) {
      try {
        mergerRef.current.disconnect();
      } catch (e) {}
      mergerRef.current = null;
    }

    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect();
      } catch (e) {}
      gainNodeRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clear buffer pools
    frequencyDataPoolRef.current = null;
    timeDomainPoolRef.current = null;
    waveformDataPoolRef.current = null;

    // Only clear currentSongId when explicitly clearing the queue
    if (clearQueue) {
      setCurrentSongId(null);
    }
    
    setIsUploadedAudioPlaying(false);
    setIsUploadedAudioPaused(false);
    setCurrentPlaybackTime(0);
    setUploadedAudioData(null);
    setWaveformData(null);
    lastFrequencyDataRef.current = null;
    
    // Clear queue only when explicitly requested
    if (clearQueue) {
      setPlayQueue([]);
      setOriginalQueue([]);
    }
    
    playbackStartTimeRef.current = 0;
    pausedAtTimeRef.current = 0;
    audioBufferRef.current = null;
  }, []);

  const handleCloseMusicPlayer = useCallback(() => {
    handleStopSong(false); // Don't clear queue or current song - let user resume later
    setShowMusicPlayer(false);
  }, [handleStopSong]);

  const handleAuthStateChange = useCallback((user: User | null, profile: UserProfile | null) => {
    const wasSignedIn = !!userRef.current;
    const isSignedIn = !!user;
    
    userRef.current = user;
    setUser(user);
    setUserProfile(profile);
    
    // No need to manually load - the useEffect will handle real-time listeners
    // Just clear music and stop audio if user signed out
    if (wasSignedIn && !isSignedIn) {
      // Only clear music and stop audio if user ACTUALLY signed out (was signed in, now not)
      setSongs([]);
      setPlaylists([]);
      handleStopSong();
    }
  }, [handleStopSong]); // Add handleStopSong to dependencies

  // Set up real-time listeners for music library
  useEffect(() => {
    if (!user) {
      return;
    }

    // Subscribe to songs
    const unsubscribeSongs = musicService.subscribeToUserSongs((userSongs) => {
      setSongs(userSongs);
      
      // Trigger analysis for songs without analysis data (process sequentially)
      const songsNeedingAnalysis = userSongs.filter(song => !song.analysis);
      if (songsNeedingAnalysis.length > 0 && !isAnalyzingRef.current) {
        isAnalyzingRef.current = true;
        analyzeMultipleSongs(songsNeedingAnalysis);
      }
    });

    // Subscribe to playlists
    const unsubscribePlaylists = musicService.subscribeToUserPlaylists((userPlaylists) => {
      setPlaylists(userPlaylists);
    });

    // Cleanup subscriptions on unmount or when user changes
    return () => {
      unsubscribeSongs();
      unsubscribePlaylists();
    };
  }, [user]); // Re-subscribe when user changes

  // Analyze multiple songs sequentially to avoid overwhelming the browser
  const analyzeMultipleSongs = async (songsToAnalyze: Song[]) => {
    for (const song of songsToAnalyze) {
      try {
        await analyzeExistingSong(song);
      } catch (error) {
        console.error('[App] Failed to analyze existing song:', song.name, error);
        // Continue with next song even if one fails
      }
    }
    isAnalyzingRef.current = false;
  };

  // Analyze existing song that's missing analysis data
  const analyzeExistingSong = async (song: Song) => {
    try {
      // Download the audio file
      const response = await fetch(song.downloadUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode audio
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Analyze
      const analysis = await audioAnalysisService.analyzeAudioBuffer(audioBuffer);
      
      // Update song with analysis
      const updatedSong = { ...song, analysis };
      await musicService.saveSong(updatedSong);
      
      analyticsService.trackFeatureUse('retroactive_analysis_success', song.name);
    } catch (error) {
      console.error('[App] Retroactive analysis failed:', error);
      analyticsService.trackError('retroactive_analysis_failed', String(error));
      throw error;
    }
  };

  const handleAddFavoriteToWorkspace = (favorite: FavoriteFrequency) => {
    const newWave = configService.favoriteToWave(favorite, nextId);
    setWaves(prev => [...prev, newWave]);
    setNextId(n => n + 1);
    analyticsService.trackFeatureUse('favorite_add_to_workspace', `freq:${favorite.freq}`);
  };

  const handleAnalyze = async (wavesToAnalyze: Wave[]) => {
    try {
      // Import the analysis service
      const { default: audioProcessingService } = await import('./services/audioProcessingService');
      
      // Perform frequency analysis
      const analysis = await audioProcessingService.analyzeWaveFrequencies(wavesToAnalyze, {
        sampleRate: 44100,
        duration: 2.0
      });
      
      setAnalysisData(analysis);
      return analysis;
    } catch (error) {
      console.error('Analysis failed:', error);
      throw error;
    }
  };

  const addCounterWave = (targetId: number) => {
    const target = waves.find(w => w.id === targetId);
    if (!target) return;
    
    const color = COLORS[(nextId - 1) % COLORS.length];
    setWaves(prev => [
      ...prev,
      { 
        id: nextId, 
        freq: target.freq, 
        amp: target.amp, 
        phase: (target.phase + 180) % 360, 
        color,
        muted: false,
        waveform: target.waveform || 'sine'
      }
    ]);
    setNextId(n => n + 1);
  };

  const handleImportFromFourier = (newWaves: Wave[]) => {
      const normalizedWaves = newWaves.map(w => ({ ...w, waveform: w.waveform || 'sine' }));
      setWaves(normalizedWaves);
      // Ensure the next ID generator is higher than the max ID we just imported
      const maxId = normalizedWaves.reduce((max, w) => Math.max(max, w.id), 0);
      setNextId(maxId + 1);
      // Ensure Wave Lab plays at real-time speed
      setSpeed(1);
      setActiveTab('interference');
  };

  const handleImportFromWavePool = () => {
      // Convert Wave Pool simulation to Wave Lab frequency layers
      // Goal: Match what users hear in Wave Pool audio
      const newWaves: Wave[] = [];
      
      // Scale to speaking frequency range (100-200 Hz) for immediate audibility
      const FREQ_SCALE = 400; // 0.01-0.5 Hz → 4-200 Hz (speaking range)
      
      const baseAmp = Math.min(100, Math.max(20, wavePoolConfig.amplitude * 40)); // 0.1-2.0 → 20-80 range
      const carrierFreq = wavePoolConfig.baseFrequency ?? wavePoolConfig.frequency;
      
      // ALWAYS include the base simulation frequency (matches audio hook behavior)
      const baseFreq = carrierFreq * FREQ_SCALE;
      newWaves.push({
        id: nextId,
        freq: baseFreq,
        amp: baseAmp,
        phase: 0,
        color: COLORS[0],
        muted: false,
        waveform: 'sine'
      });
      
      // Add active emitters as additional frequency layers
      let idOffset = 1;
      const uniqueEmitterFreqs = new Map<number, { phase: number; count: number }>();
      
      wavePoolEmitters.forEach(emitter => {
        if (emitter.active) {
          const freq = emitter.frequency;
          if (!uniqueEmitterFreqs.has(freq)) {
            uniqueEmitterFreqs.set(freq, { phase: emitter.phase, count: 1 });
          } else {
            uniqueEmitterFreqs.get(freq)!.count++;
          }
        }
      });
      
      // Add each unique emitter frequency (only if different from base)
      uniqueEmitterFreqs.forEach((data, freq) => {
        const scaledFreq = freq * FREQ_SCALE;
        // Only add if significantly different from base frequency to avoid redundancy
        if (Math.abs(scaledFreq - baseFreq) > 2) { // 2 Hz threshold
          newWaves.push({
            id: nextId + idOffset,
            freq: scaledFreq,
            amp: Math.min(100, baseAmp * 0.6 * Math.sqrt(data.count)), // Slightly lower for harmonics
            phase: (data.phase * 180 / Math.PI + 360) % 360, // Radians → degrees
            color: COLORS[idOffset % COLORS.length],
            muted: false,
            waveform: 'sine'
          });
          idOffset++;
        }
      });

      setWaves(newWaves);
      const maxId = newWaves.reduce((max, w) => Math.max(max, w.id), 0);
      setNextId(maxId + 1);
        // Ensure Wave Lab plays at real-time speed
        setSpeed(1);
      setActiveTab('interference');
      
      analyticsService.trackFeatureUse('wavepool_export_to_wavelab', `sources:${newWaves.length}_pattern:${wavePoolConfig.pattern}`);
  };

  const handleAudioDataUpdate = (audioData: Float32Array | null, isPlaying: boolean) => {
    setUploadedAudioData(audioData);
    setIsUploadedAudioPlaying(isPlaying);
  };

  // Music Library Handlers
  const handleSongUploaded = async (song: Song) => {
    try {
      // Save to Firestore
      await musicService.saveSong(song);
      
      // Add to local state (only if not already present)
      setSongs(prev => {
        if (prev.some(s => s.id === song.id)) {
          console.warn('Song already in list, skipping duplicate:', song.id);
          return prev;
        }
        return [...prev, song];
      });
      analyticsService.trackFeatureUse('song_added_to_library', song.name);
    } catch (error) {
      console.error('Failed to save song metadata:', error);
      alert('Song uploaded but failed to save metadata. Please try again.');
    }
  };

  const handlePlaySong = useCallback(async (requestedSongId: string, options?: {
    context?: 'library' | 'playlist' | 'queue';
    playlistSongs?: Song[];
    shuffle?: boolean;
  }) => {
    let songId = requestedSongId;
    
    // Handle queue building based on context (do this BEFORE isLoadingSong check)
    if (options?.context === 'library' || options?.context === 'playlist') {
      // When playing from library or playlist, build/rebuild the queue
      const queueSongs = options.playlistSongs || songs;
      
      // Determine shuffle behavior:
      // - shuffle: true (explicit) = Shuffle button clicked, shuffle queue and play first
      // - shuffle: false (explicit) = Play All button clicked, play in order
      // - shuffle: undefined = Single song clicked, play that song (honor user's click)
      const explicitShuffleRequest = options.shuffle === true;
      const shouldShuffle = options.shuffle !== undefined ? options.shuffle : shuffleEnabled;
      
      if (shouldShuffle) {
        // Apply shuffle - shuffle the entire array
        const shuffleArray = <T,>(array: T[]): T[] => {
          const shuffled = [...array];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          return shuffled;
        };

        const newQueue = shuffleArray(queueSongs);
        setPlayQueue(newQueue);
        setOriginalQueue(queueSongs);
        
        // Only override songId if this was an explicit Shuffle button click
        // If user clicked a specific song, play that song even if shuffle is enabled
        if (explicitShuffleRequest && newQueue.length > 0) {
          songId = newQueue[0].id;
        }
      } else {
        // No shuffle
        setPlayQueue(queueSongs);
        setOriginalQueue(queueSongs);
      }
    }
    // If context is 'queue', don't modify the queue at all

    const song = songs.find(s => s.id === songId);
    if (!song) return;

    // Prevent double-loading: if already loading a song, ignore the actual load
    // But we've already updated the queue above if needed
    if (isLoadingSong) {
      console.log('Song already loading, queue updated but skipping reload');
      return;
    }

    // Show loading state
    setIsLoadingSong(true);

    // Stop current playback if any - cancel old animation frame first
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (sourceNodeRef.current) {
      handleStopSong();
    }

    try {
      // Initialize audio context
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        audioContextRef.current = new AudioContext();
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Fetch audio file from Firebase Storage
      const response = await fetch(song.downloadUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      audioBufferRef.current = audioBuffer; // Store for pause/resume

      // Create source node
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;

      // REAL-TIME FREQUENCY ANALYSIS SYSTEM
      // Instead of creating a separate visualizer, we extract frequency components
      // from the audio in real-time and map them to the existing wave visualization system.
      // This allows uploaded audio to be visualized through all existing modes:
      // interference patterns, Lissajous curves, 3D XYZ, Chladni plates, etc.
      
      // Check if audio is stereo
      const isStereo = audioBuffer.numberOfChannels >= 2;
      
      // Create analyser nodes for stereo channel separation
      const analyserLeft = audioContextRef.current.createAnalyser();
      analyserLeft.fftSize = 4096; // Reduced from 4096 for lower memory pressure (still good resolution)
      analyserLeft.smoothingTimeConstant = 0.0; // No smoothing - raw waveform data
      
      const analyserRight = audioContextRef.current.createAnalyser();
      analyserRight.fftSize = 4096;
      analyserRight.smoothingTimeConstant = 0.0; // No smoothing - raw waveform data

      // Pre-allocate buffer pools to avoid creating new arrays every frame
      frequencyDataPoolRef.current = {
        left: new Uint8Array(analyserLeft.frequencyBinCount),
        right: new Uint8Array(analyserLeft.frequencyBinCount)
      };
      timeDomainPoolRef.current = {
        left: new Float32Array(analyserLeft.fftSize),
        right: new Float32Array(analyserLeft.fftSize)
      };
      // Create a reusable waveform data object to avoid creating new objects every frame
      waveformDataPoolRef.current = {
        left: new Float32Array(analyserLeft.fftSize),
        right: new Float32Array(analyserLeft.fftSize)
      };
      lastStateUpdateTimeRef.current = 0;

      // Create splitter and merger for stereo processing
      const splitter = audioContextRef.current.createChannelSplitter(2);
      const merger = audioContextRef.current.createChannelMerger(2);
      
      // Create gain node for volume control
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = playbackVolume;

      // Connect nodes for stereo analysis
      if (isStereo) {
        source.connect(splitter);
        splitter.connect(analyserLeft, 0); // Left channel
        splitter.connect(analyserRight, 1); // Right channel
        splitter.connect(merger, 0, 0);
        splitter.connect(merger, 1, 1);
        merger.connect(gainNode);
      } else {
        // Mono audio - use same analyser for both channels
        source.connect(analyserLeft);
        analyserLeft.connect(gainNode);
      }
      
      gainNode.connect(audioContextRef.current.destination);

      // Store references (store left analyser as main)
      sourceNodeRef.current = source;
      analyserRef.current = analyserLeft;
      analyserLeftRef.current = analyserLeft;
      analyserRightRef.current = analyserRight;
      splitterRef.current = splitter;
      mergerRef.current = merger;
      isStereoRef.current = isStereo;
      gainNodeRef.current = gainNode;

      // Handle end of playback
      source.onended = () => handleSongEndedRef.current();

      // Start playback
      source.start(0);
      playbackStartTimeRef.current = audioContextRef.current.currentTime;
      pausedAtTimeRef.current = 0;
      setCurrentSongId(songId);
      setShowMusicPlayer(true);
      setIsUploadedAudioPlaying(true);
      setIsUploadedAudioPaused(false);
      setCurrentPlaybackTime(0);
      setIsLoadingSong(false);
      
      // Update song duration to match actual decoded buffer (may differ slightly from upload)
      if (Math.abs(song.duration - audioBuffer.duration) > 0.1) {
        setSongs(prevSongs => prevSongs.map(s => 
          s.id === songId ? { ...s, duration: audioBuffer.duration } : s
        ));
      }
      
      console.log('Audio playback started:', {
        songId,
        isStereo,
        duration: audioBuffer.duration,
        storedDuration: song.duration,
        channels: audioBuffer.numberOfChannels
      });

      // Extract frequency components and map to existing wave system with stereo separation
      const updateAudioData = () => {
        try {
          if (!analyserLeftRef.current) {
            setUploadedAudioData(null);
            setWaveformData(null);
            lastFrequencyDataRef.current = null;
            // Keep the loop running even when paused so it resumes automatically
            animationFrameRef.current = requestAnimationFrame(updateAudioData);
            return;
          }
          
          // When paused, persist the last frequency data instead of clearing it
          if (!sourceNodeRef.current) {
            if (lastFrequencyDataRef.current) {
              setUploadedAudioData(lastFrequencyDataRef.current);
            }
            // Don't clear waveformData when paused - keep last frame visible
            // Keep the loop running even when paused so it resumes automatically
            animationFrameRef.current = requestAnimationFrame(updateAudioData);
            return;
          }

          // Use pre-allocated buffer pools instead of creating new arrays
          if (!frequencyDataPoolRef.current || !timeDomainPoolRef.current) {
            animationFrameRef.current = requestAnimationFrame(updateAudioData);
            return;
          }

          const pooledFreqData = frequencyDataPoolRef.current;
          const pooledTimeDomainData = timeDomainPoolRef.current;

          // Reuse pooled buffers - just fill with current data
          analyserLeftRef.current.getByteFrequencyData(pooledFreqData.left);
          if (isStereoRef.current && analyserRightRef.current) {
            analyserRightRef.current.getByteFrequencyData(pooledFreqData.right);
          } else {
            pooledFreqData.right.set(pooledFreqData.left);
          }

          // Get time domain (waveform) data for oscilloscope XY mode
          analyserLeftRef.current.getFloatTimeDomainData(pooledTimeDomainData.left);
          if (isStereoRef.current && analyserRightRef.current) {
            analyserRightRef.current.getFloatTimeDomainData(pooledTimeDomainData.right);
          } else {
            // Mono: copy left to right
            pooledTimeDomainData.right.set(pooledTimeDomainData.left);
          }

          // Always update waveform data every frame (visualizers need continuous updates)
          // Copy to the reusable waveform data object to avoid creating new objects every frame
          if (waveformDataPoolRef.current) {
            waveformDataPoolRef.current.left.set(pooledTimeDomainData.left);
            waveformDataPoolRef.current.right.set(pooledTimeDomainData.right);
            setWaveformData(waveformDataPoolRef.current);
          }

          // Throttle complex peak analysis to 30fps max (~33ms) to reduce GC pressure
          const now = performance.now();
          const shouldUpdate = (now - lastStateUpdateTimeRef.current) > 33;
          
          if (!shouldUpdate) {
            // Skip peak analysis this frame, but keep animation loop running
            animationFrameRef.current = requestAnimationFrame(updateAudioData);
            return;
          }

          // Update lastStateUpdateTimeRef for next throttle check
          lastStateUpdateTimeRef.current = now;

        // Extract top frequency components and map to waves
        const sampleRate = audioContextRef.current!.sampleRate;
        const nyquist = sampleRate / 2;
        const binSize = nyquist / pooledFreqData.left.length;

          // Find peaks (local maxima) in frequency data
          const findPeaks = (frequencyData: Uint8Array) => {
            const peaks: { freq: number; amp: number }[] = [];
            const minAmplitude = 20;
            const minPeakDistance = 8; // Minimum bins between peaks to avoid clustering
          
            let lastPeakIndex = -minPeakDistance;
          
            for (let i = 1; i < frequencyData.length - 1; i++) {
              const amplitude = frequencyData[i];
              const prev = frequencyData[i - 1];
              const next = frequencyData[i + 1];
            
              // Is this a local maximum above threshold and far enough from last peak?
              if (amplitude > minAmplitude && 
                  amplitude >= prev && 
                  amplitude >= next &&
                  (i - lastPeakIndex) >= minPeakDistance) {
                const frequency = i * binSize;
                if (frequency >= 20 && frequency <= 20000) {
                  peaks.push({ freq: frequency, amp: amplitude });
                  lastPeakIndex = i;
                }
              }
            }
            return peaks;
          };

          const leftFreqs = findPeaks(pooledFreqData.left);
          const rightFreqs = isStereoRef.current ? findPeaks(pooledFreqData.right) : [];

          // Sort by amplitude and take top frequencies
          leftFreqs.sort((a, b) => b.amp - a.amp);
          rightFreqs.sort((a, b) => b.amp - a.amp);
        
          const maxPerChannel = 4; // Fewer peaks to reduce jagged visuals
          const topLeft = leftFreqs.slice(0, maxPerChannel);
          const topRight = rightFreqs.slice(0, maxPerChannel);

        // Match frequencies between channels
        const sharedPeaks: { freq: number; amp: number }[] = [];
        const leftOnlyPeaks: { freq: number; amp: number }[] = [];
        const rightOnlyPeaks: { freq: number; amp: number }[] = [];

        if (isStereoRef.current) {
          topLeft.forEach(leftPeak => {
            const matchingRight = topRight.find(rightPeak => 
              Math.abs(rightPeak.freq - leftPeak.freq) / leftPeak.freq < 0.05
            );
            if (matchingRight && Math.abs(leftPeak.amp - matchingRight.amp) < 30) {
              sharedPeaks.push({ 
                freq: (leftPeak.freq + matchingRight.freq) / 2, 
                amp: (leftPeak.amp + matchingRight.amp) / 2 
              });
            } else {
              leftOnlyPeaks.push(leftPeak);
            }
          });

          topRight.forEach(rightPeak => {
            const matchingLeft = topLeft.find(leftPeak => 
              Math.abs(leftPeak.freq - rightPeak.freq) / rightPeak.freq < 0.05
            );
            if (!matchingLeft) {
              rightOnlyPeaks.push(rightPeak);
            }
          });
        } else {
          sharedPeaks.push(...topLeft);
        }

        // Map frequencies to wave parameters with channel information
        // Format: [freq, amp, channel, freq, amp, channel, ...]
        // channel: 0 = both, 1 = left, 2 = right
        const allPeaks: { freq: number; amp: number; channel: number }[] = [
          ...sharedPeaks.map(p => ({ ...p, channel: 0 })),
          ...leftOnlyPeaks.map(p => ({ ...p, channel: 1 })),
          ...rightOnlyPeaks.map(p => ({ ...p, channel: 2 }))
        ];

        // Only generate new mapped data if we're updating (throttled)
        if (shouldUpdate) {
          const mappedData = new Float32Array(allPeaks.length * 3);
          allPeaks.forEach((peak, i) => {
            mappedData[i * 3] = peak.freq; // Frequency
            mappedData[i * 3 + 1] = peak.amp / 255; // Normalized amplitude (0-1)
            mappedData[i * 3 + 2] = peak.channel; // Channel (0=both, 1=left, 2=right)
          });

          // Light temporal smoothing to reduce jagged motion in visuals
          if (lastFrequencyDataRef.current && lastFrequencyDataRef.current.length === mappedData.length) {
            const prev = lastFrequencyDataRef.current;
            const smoothFactor = 0.4; // 0 = no smoothing, 1 = fully previous
            for (let i = 0; i < mappedData.length; i += 3) {
              // Smooth frequency and amplitude; keep channel id from current frame
              mappedData[i] = mappedData[i] * (1 - smoothFactor) + prev[i] * smoothFactor;
              mappedData[i + 1] = mappedData[i + 1] * (1 - smoothFactor) + prev[i + 1] * smoothFactor;
            }
          }

          // Store the frequency data so it can be persisted when paused
          lastFrequencyDataRef.current = mappedData;
          setUploadedAudioData(mappedData);
          
          // Only warn about no peaks once every 5 seconds to avoid spam
          if (mappedData.length === 0) {
            const now = Date.now();
            if (now - lastPeakWarningRef.current > 5000) {
              console.warn('No frequency peaks detected in audio');
              lastPeakWarningRef.current = now;
            }
          }
        }

        animationFrameRef.current = requestAnimationFrame(updateAudioData);
      } catch (error) {
        console.error('Error updating audio data:', error);
        // Continue with empty data to avoid white screen
        setUploadedAudioData(new Float32Array(0));
        animationFrameRef.current = requestAnimationFrame(updateAudioData);
      }
      };
      
      console.log('Starting audio analysis loop with optimized buffer pooling');
      updateAudioData();

      analyticsService.trackFeatureUse('song_playback_start', song.name);
    } catch (error: any) {
      console.error('Failed to play song:', error);
      setIsLoadingSong(false); // Reset loading state on error
      alert('Failed to play song. The file may be corrupted or unavailable.');
      analyticsService.trackError('song_playback_failed', error?.message || 'Unknown error');
    }
  }, [songs, playbackVolume, handleStopSong, isLoadingSong]);

  const handlePlayPause = async () => {
    if (!currentSongId) return;

    // If there's a currentSongId but no loaded buffer, load and play the song
    if (!audioBufferRef.current) {
      await handlePlaySong(currentSongId);
      return;
    }

    if (!audioContextRef.current) return;

    if (isUploadedAudioPaused) {
      // Resume playback
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      if (audioBufferRef.current) {
        // Create new source node (they can only be used once)
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;

        // Reconnect to existing audio graph (analysers, gain)
        if (isStereoRef.current && splitterRef.current && mergerRef.current) {
          // Stereo path: source -> splitter -> analysers -> merger -> gain -> destination
          source.connect(splitterRef.current);
        } else if (analyserRef.current) {
          // Mono path: source -> analyser -> gain -> destination
          source.connect(analyserRef.current);
        }

        // Start from paused position
        const offset = pausedAtTimeRef.current;
        source.start(0, offset);
        playbackStartTimeRef.current = audioContextRef.current.currentTime - offset;
        
        sourceNodeRef.current = source;
        source.onended = () => handleSongEndedRef.current();
        
        // Restart the visualization loop (will be started by updateAudioData call below)
      }

      setIsUploadedAudioPlaying(true);
      setIsUploadedAudioPaused(false);
    } else {
      // Pause playback
      if (sourceNodeRef.current && audioContextRef.current) {
        pausedAtTimeRef.current = audioContextRef.current.currentTime - playbackStartTimeRef.current;
        
        try {
          sourceNodeRef.current.onended = null; // Critical: prevent triggering handleSongEnded
          sourceNodeRef.current.stop();
          sourceNodeRef.current.disconnect();
        } catch (e) {
          // Already stopped
        }
        sourceNodeRef.current = null;
      }

      // Keep isPlaying true but set isPaused to true
      setIsUploadedAudioPaused(true);
    }
  };

  const handleNext = useCallback(() => {
    if (!currentSongId || playQueue.length === 0) return;

    const currentIndex = playQueue.findIndex(s => s.id === currentSongId);
    let nextIndex = currentIndex + 1;

    // Handle reaching end of queue
    if (nextIndex >= playQueue.length) {
      if (loopMode === 'all') {
        nextIndex = 0; // Loop back to start
      } else if (shuffleEnabled) {
        nextIndex = 0; // In shuffle mode, loop back to start of shuffled queue
      } else {
        handleStopSong();
        return;
      }
    }

    const nextSong = playQueue[nextIndex];
    handlePlaySong(nextSong.id);
  }, [currentSongId, playQueue, loopMode, shuffleEnabled, handleStopSong, handlePlaySong]);

  const handleSongEnded = useCallback(() => {
    if (loopMode === 'one') {
      // Replay the same song
      if (currentSongId) {
        handleSeekRef.current(0);
      }
    } else if (loopMode === 'all' || shuffleEnabled || playQueue.findIndex(s => s.id === currentSongId) < playQueue.length - 1) {
      // Play next song (handles loop all, shuffle mode, or not at the end)
      handleNext();
    } else {
      // Stop playback (only when not looping, not shuffling, and at the end)
      handleStopSong();
    }
  }, [loopMode, shuffleEnabled, currentSongId, playQueue, handleStopSong, handleNext]);

  // Store handleSongEnded in ref for use in handleSeek
  useEffect(() => {
    handleSongEndedRef.current = handleSongEnded;
  }, [handleSongEnded]);

  const handleSeek = useCallback(async (time: number) => {
    if (!audioContextRef.current || !audioBufferRef.current || !currentSongId) return;

    // Ensure AudioContext is running before seeking
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    // Use ref for most accurate paused state
    const wasPlaying = !isPausedRef.current;

    // Stop current playback - CLEAR onended FIRST to prevent triggering song change
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.onended = null; // Critical: prevent triggering handleSongEnded
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      } catch (e) {
        // Already stopped
      }
      sourceNodeRef.current = null;
    }

    // Update time immediately for responsive UI
    setCurrentPlaybackTime(time);

    // If was playing, restart from new position
    if (wasPlaying) {
      // Ensure AudioContext is still running after the stop
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBufferRef.current;

      // Reconnect to existing audio graph (analysers, gain)
      if (isStereoRef.current && splitterRef.current && mergerRef.current) {
        // Stereo path: source -> splitter -> analysers -> merger -> gain -> destination
        source.connect(splitterRef.current);
      } else if (analyserRef.current) {
        // Mono path: source -> analyser -> gain -> destination
        source.connect(analyserRef.current);
      }

      source.start(0, time);
      playbackStartTimeRef.current = audioContextRef.current.currentTime - time;
      sourceNodeRef.current = source;
      source.onended = () => handleSongEndedRef.current();
      
      // Trigger time update interval restart
      setSeekTrigger(prev => prev + 1);
    } else {
      // Paused: analyze the frequency at this time position
      pausedAtTimeRef.current = time;
      
      try {
        // Analyze a 100ms window at the seeked position
        const analysis = await audioAnalysisService.analyzeTimeRange(
          audioBufferRef.current,
          time,
          0.1
        );
        
        // Update the visualization with the analyzed frequency data
        if (analysis.frequencies.length > 0) {
          lastFrequencyDataRef.current = analysis.frequencies;
          setUploadedAudioData(analysis.frequencies);
        }
      } catch (error) {
        console.warn('Failed to analyze audio at seek position:', error);
      }
    }
  }, [currentSongId]);

  // Store handleSeek in ref for use in handleSongEnded
  useEffect(() => {
    handleSeekRef.current = handleSeek;
  }, [handleSeek]);

  const handleTogglePanels = useCallback((fullBrowser: boolean) => {
    setIsVisualizerFullBrowser(fullBrowser);
  }, []);

  const handleVolumeChange = (volume: number) => {
    setPlaybackVolume(volume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume;
    }
  };

  const handlePrevious = useCallback(() => {
    if (!currentSongId || playQueue.length === 0) return;

    const currentIndex = playQueue.findIndex(s => s.id === currentSongId);
    
    if (currentPlaybackTime > 3) {
      // If more than 3 seconds into the song, restart it
      handleSeek(0);
    } else {
      // Go to previous song
      let prevIndex = currentIndex - 1;
      if (prevIndex < 0) {
        if (loopMode === 'all') {
          prevIndex = playQueue.length - 1;
        } else if (shuffleEnabled) {
          prevIndex = playQueue.length - 1; // In shuffle mode, loop to end of shuffled queue
        } else {
          return; // No previous song
        }
      }

      const prevSong = playQueue[prevIndex];
      handlePlaySong(prevSong.id);
    }
  }, [currentSongId, playQueue, currentPlaybackTime, loopMode, shuffleEnabled, handleSeek, handlePlaySong]);

  const handleLoopModeChange = (mode: 'none' | 'one' | 'all') => {
    setLoopMode(mode);
  };

  const handleAddToQueue = (songId: string) => {
    const song = songs.find(s => s.id === songId);
    if (!song) return;

    // Check if song is already in queue
    const alreadyInQueue = playQueue.some(s => s.id === songId);
    if (alreadyInQueue) {
      console.log('Song already in queue:', song.name);
      // Still show the queue panel even if song is already in queue
      setShowQueuePanel(true);
      return;
    }

    if (shuffleEnabled) {
      // In shuffle mode: add to original queue and also to end of current queue
      setOriginalQueue(prev => [...prev, song]);
      setPlayQueue(prev => [...prev, song]);
    } else {
      // Normal mode: add to end of both queues
      setPlayQueue(prev => [...prev, song]);
      setOriginalQueue(prev => [...prev, song]);
    }

    analyticsService.trackFeatureUse('song_added_to_queue', song.name);
    
    // Show the queue panel when a song is added
    setShowQueuePanel(true);
  };

  const handleRemoveFromQueue = (songId: string) => {
    // Don't allow removing the currently playing song
    if (songId === currentSongId) return;

    setPlayQueue(prev => prev.filter(s => s.id !== songId));
    
    // Always update original queue (whether shuffling or not)
    setOriginalQueue(prev => prev.filter(s => s.id !== songId));

    analyticsService.trackFeatureUse('song_removed_from_queue');
  };

  const handleClearQueue = () => {
    setPlayQueue([]);
    setOriginalQueue([]);
    analyticsService.trackFeatureUse('queue_cleared');
  };

  const handleShuffleChange = (enabled: boolean) => {
    setShuffleEnabled(enabled);
  };

  const handleShuffleToggle = () => {
    if (!shuffleEnabled) {
      // Enable shuffle - shuffle the queue but keep current song at current position
      const currentIndex = playQueue.findIndex(s => s.id === currentSongId);
      
      // Shuffle before and after separately
      const shuffleArray = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      let newQueue: Song[];
      
      // If no current song or current song not in queue, just shuffle the entire queue
      if (currentIndex === -1) {
        newQueue = shuffleArray(playQueue);
      } else {
        // Keep current song at its position and shuffle the rest
        const beforeCurrent = playQueue.slice(0, currentIndex);
        const afterCurrent = playQueue.slice(currentIndex + 1);
        
        newQueue = [
          ...shuffleArray(beforeCurrent),
          playQueue[currentIndex],
          ...shuffleArray(afterCurrent)
        ];
      }
      
      setOriginalQueue(playQueue);
      setPlayQueue(newQueue);
      setShuffleEnabled(true);
    } else {
      // Disable shuffle - restore original order
      setPlayQueue(originalQueue);
      setShuffleEnabled(false);
    }
  };

  // Update playback time continuously
  useEffect(() => {
    // Keep ref in sync with state
    isPausedRef.current = isUploadedAudioPaused;
  }, [isUploadedAudioPaused]);

  useEffect(() => {
    // Don't run if not playing or if paused
    if (!isUploadedAudioPlaying || isUploadedAudioPaused) {
      return;
    }

    let animationId: number;
    let lastUpdateTime = performance.now();
    
    // Use requestAnimationFrame for smoother, more reliable updates
    // This works better than setInterval because it's tied to the browser's render cycle
    const updateTime = () => {
      const now = performance.now();
      
      // Only update if enough time has passed (throttle to ~60fps)
      if (now - lastUpdateTime >= 16) {
        lastUpdateTime = now;
        
        if (audioContextRef.current && sourceNodeRef.current) {
          // Check if AudioContext is still running
          if (audioContextRef.current.state === 'running') {
            const elapsed = audioContextRef.current.currentTime - playbackStartTimeRef.current;
            // Clamp to buffer duration to avoid going over 100%
            const maxDuration = audioBufferRef.current?.duration || Infinity;
            const clampedTime = Math.max(0, Math.min(elapsed, maxDuration));
            setCurrentPlaybackTime(clampedTime);
          } else if (audioContextRef.current.state === 'suspended') {
            // Try to resume the AudioContext if it got suspended
            audioContextRef.current.resume().catch(() => {
              // Ignore errors - user interaction may be required
            });
          }
        }
      }
      
      animationId = requestAnimationFrame(updateTime);
    };
    
    animationId = requestAnimationFrame(updateTime);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isUploadedAudioPlaying, isUploadedAudioPaused, currentSongId, seekTrigger]);

  // Initialize play queue when song starts - ONLY as a fallback for app startup
  // when we have a currentSongId from localStorage but no queue
  // Do NOT include shuffleEnabled in dependencies - queue building is handled in handlePlaySong
  useEffect(() => {
    if (currentSongId && songs.length > 0 && playQueue.length === 0 && originalQueue.length === 0) {
      // This is a true fallback - only runs on app startup with restored currentSongId
      // but no queue. Build queue from all songs.
      if (shuffleEnabled) {
        const currentIndex = songs.findIndex(s => s.id === currentSongId);
        const shuffleArray = <T,>(array: T[]): T[] => {
          const shuffled = [...array];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          return shuffled;
        };

        let newQueue: Song[];
        if (currentIndex === -1) {
          newQueue = shuffleArray(songs);
        } else {
          const beforeCurrent = songs.slice(0, currentIndex);
          const afterCurrent = songs.slice(currentIndex + 1);
          newQueue = [
            ...shuffleArray(beforeCurrent),
            songs[currentIndex],
            ...shuffleArray(afterCurrent)
          ];
        }
        setPlayQueue(newQueue);
        setOriginalQueue(songs);
      } else {
        setPlayQueue(songs);
        setOriginalQueue(songs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSongId, songs.length]);

  const handleDeleteSong = async (songId: string) => {
    const song = songs.find(s => s.id === songId);
    if (!song) return;

    if (currentSongId === songId) {
      handleCloseMusicPlayer();
    }

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, song.storagePath);
      await deleteObject(storageRef);
      
      // Delete metadata from Firestore
      await musicService.deleteSong(songId);
      
      // Remove from state
      setSongs(prev => prev.filter(s => s.id !== songId));
      
      // Remove from all playlists
      setPlaylists(prev => prev.map(p => ({
        ...p,
        songIds: p.songIds.filter(id => id !== songId)
      })));

      analyticsService.trackFeatureUse('song_delete_success', song.name);
    } catch (error: any) {
      console.error('Failed to delete song:', error);
      alert('Failed to delete song. Please try again.');
      analyticsService.trackError('song_delete_failed', error?.message || 'Unknown error');
    }
  };

  const handleUpdateSongMetadata = async (songId: string, metadata: Partial<Song>) => {
    try {
      await musicService.updateSong(songId, metadata);
      
      // Update in state
      setSongs(prev => prev.map(s => 
        s.id === songId ? { ...s, ...metadata } : s
      ));
      
      analyticsService.trackFeatureUse('song_metadata_update_success', songId);
    } catch (error: any) {
      console.error('Failed to update song metadata:', error);
      alert('Failed to update song metadata. Please try again.');
      analyticsService.trackError('song_metadata_update_failed', error?.message || 'Unknown error');
    }
  };

  const handleCreatePlaylist = async (name: string) => {
    const newPlaylist: Playlist = {
      id: `playlist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      songIds: [],
      createdAt: Date.now()
    };
    
    try {
      await musicService.savePlaylist(newPlaylist);
      // No need to update state - the real-time listener will handle it
    } catch (error) {
      console.error('Failed to create playlist:', error);
      alert('Failed to create playlist. Please try again.');
    }
  };

  const handleDeletePlaylist = async (playlistId: string) => {
    try {
      await musicService.deletePlaylist(playlistId);
      // No need to update state - the real-time listener will handle it
    } catch (error) {
      console.error('Failed to delete playlist:', error);
      alert('Failed to delete playlist. Please try again.');
    }
  };

  const handleAddToPlaylist = async (songIdOrPlaylistId: string, playlistIdOrSongIds: string | string[]) => {
    // Support both individual and batch operations
    const isBatch = Array.isArray(playlistIdOrSongIds);
    const songId = isBatch ? songIdOrPlaylistId : songIdOrPlaylistId;
    const playlistId = isBatch ? songIdOrPlaylistId : (playlistIdOrSongIds as string);
    const songIds = isBatch ? (playlistIdOrSongIds as string[]) : [songId];
    
    try {
      // Get the latest playlist data from Firestore to avoid stale state issues with bulk operations
      const playlist = await musicService.getPlaylist(playlistId);
      if (!playlist) return;

      // Filter out songs that are already in the playlist
      const newSongIds = songIds.filter(id => !playlist.songIds.includes(id));
      if (newSongIds.length === 0) {
        return;
      }
      
      const updatedSongIds = [...playlist.songIds, ...newSongIds];
      
      await musicService.updatePlaylist(playlistId, { songIds: updatedSongIds });
      // No need to update state - the real-time listener will handle it
    } catch (error) {
      console.error('Failed to add song to playlist:', error);
      alert('Failed to add song to playlist. Please try again.');
    }
  };

  const handleRemoveFromPlaylist = async (songIdOrPlaylistId: string, playlistIdOrSongIds: string | string[]) => {
    // Support both individual and batch operations
    const isBatch = Array.isArray(playlistIdOrSongIds);
    const songId = isBatch ? songIdOrPlaylistId : songIdOrPlaylistId;
    const playlistId = isBatch ? songIdOrPlaylistId : (playlistIdOrSongIds as string);
    const songIds = isBatch ? new Set(playlistIdOrSongIds as string[]) : new Set([songId]);
    
    try {
      // Get the latest playlist data from Firestore to avoid stale state issues with bulk operations
      const playlist = await musicService.getPlaylist(playlistId);
      if (!playlist) return;

      const updatedSongIds = playlist.songIds.filter(id => !songIds.has(id));
      
      await musicService.updatePlaylist(playlistId, { songIds: updatedSongIds });
      // No need to update state - the real-time listener will handle it
    } catch (error) {
      console.error('Failed to remove song from playlist:', error);
      alert('Failed to remove song from playlist. Please try again.');
    }
  };

  // --- PHYSICS PRESETS MOVED TO CONFIG MANAGER ---
  // Previously: applyPreset function - now handled by ConfigManager component

  return (
    <ThemeProvider>
      <div className="flex flex-col h-screen font-sans text-gray-800 bg-gray-50/50 overflow-x-hidden">
      {/* Header - Full Width */}
      {!isVisualizerFullBrowser && (
      <header className="flex-none bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm backdrop-blur-md bg-opacity-90">
        <div className="px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsPanelOpen(!isPanelOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle panel"
            >
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="2" fill="currentColor"/>
                  <circle cx="12" cy="12" r="5" opacity="0.6"/>
                  <circle cx="12" cy="12" r="8" opacity="0.4"/>
                  <circle cx="12" cy="12" r="11" opacity="0.2"/>
                </svg>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900">Resynth Lab</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Auth Panel in top-right (only when not signed in) */}
            {!user && (
              <AuthPanel onAuthStateChange={handleAuthStateChange} />
            )}
            
            {/* Music Player button */}
            {user && (
              <button
                onClick={() => setShowMusicPlayer(true)}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
                title="Open Music Player"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
                Music
              </button>
            )}
            
            {/* Load button */}
            {user && (
              <button
                onClick={() => {
                  setIsPanelRightOpen(true);
                  setIsLayersExpanded(false);
                  setIsPresetsExpanded(false);
                  setIsFavoritesExpanded(false);
                  setIsSavedConfigsExpanded(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                title="Load Configuration"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Load
              </button>
            )}
            
            {/* Save and Save As buttons */}
            {user && (
              <>
                <button
                  onClick={handleQuickSave}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                  title="Quick Save"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Save
                </button>
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors"
                  title="Save As"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Save As
                </button>
              </>
            )}
            
            {/* Right Panel Toggle */}
            <button
              onClick={() => setIsPanelRightOpen(!isPanelRightOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle tools panel"
            >
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </button>
          </div>
        </div>
      </header>
      )}

      {/* Content Area with Sidebar and Main */}
      <div className="flex flex-1 overflow-hidden overflow-x-hidden relative">
        {/* Collapse Button - positioned on parent to avoid overflow issues */}
        {!isVisualizerFullBrowser && (
        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          className={`absolute top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white border border-gray-200 rounded-full shadow-lg hover:bg-gray-50 flex items-center justify-center transition-all duration-300 ${
            isPanelOpen ? 'left-[304px] opacity-100 delay-300 pointer-events-auto' : 'left-4 opacity-100 pointer-events-auto'
          }`}
          style={{ zIndex: 9999 }}
          aria-label="Toggle panel"
        >
          <svg className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${
            isPanelOpen ? '' : 'rotate-180'
          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        )}
        
        {/* Right Panel Collapse Button */}
        {!isVisualizerFullBrowser && (
        <button
          onClick={() => setIsPanelRightOpen(!isPanelRightOpen)}
          className={`absolute top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white border border-gray-200 rounded-full shadow-lg hover:bg-gray-50 flex items-center justify-center transition-all duration-300 ${
            isPanelRightOpen ? 'right-[304px] opacity-100 delay-300 pointer-events-auto' : 'right-4 opacity-100 pointer-events-auto'
          }`}
          style={{ zIndex: 9999 }}
          aria-label="Toggle tools panel"
        >
          <svg className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${
            isPanelRightOpen ? '' : 'rotate-180'
          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        )}
        
        {/* Left Sidebar Panel */}
        {!isVisualizerFullBrowser && (
        <aside 
          className={`flex-none bg-white border-r border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
            isPanelOpen ? 'w-80' : 'w-0'
          } overflow-hidden flex flex-col relative z-30 h-full`}
        >
          <div className="w-80 flex flex-col h-full overflow-hidden">
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Navigation Tabs */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Mode</h3>
              <nav className="flex flex-col gap-2">
                <button
                  onClick={() => setActiveTab('interference')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all text-left flex items-center gap-2 ${
                    activeTab === 'interference' 
                    ? 'bg-indigo-600 text-white shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                  Wave Lab
                </button>
                <button
                  onClick={() => setActiveTab('fourier')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all text-left flex items-center gap-2 ${
                    activeTab === 'fourier' 
                    ? 'bg-indigo-600 text-white shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Fourier Drawing
                </button>
                <button
                  onClick={() => setActiveTab('music')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all text-left flex items-center gap-2 ${
                    activeTab === 'music' 
                    ? 'bg-indigo-600 text-white shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                  </svg>
                  Music Library
                </button>
                <button
                  onClick={() => setActiveTab('wavepool')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all text-left flex items-center gap-2 ${
                    activeTab === 'wavepool' 
                    ? 'bg-indigo-600 text-white shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Wave Pool
                </button>
              </nav>
            </div>

            {/* Info Section */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Info
              </h3>
              <p className="text-sm text-gray-600">Use the right panel to manage frequencies, load presets, and access saved configurations.</p>
            </div>

            {/* Tool Buttons */}
            {user && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  Tools
                </h3>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setShowAnalysis(true)}
                    className="flex items-center gap-2 px-4 py-3 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Analyze Frequencies
                  </button>
                  <button
                    onClick={() => setShowAudioProcessor(true)}
                    className="flex items-center gap-2 px-4 py-3 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    Audio Processing
                  </button>
                </div>
              </div>
            )}

            {/* Music Panel Section */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => setIsMusicExpanded(!isMusicExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <svg className="h-4 w-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                  </svg>
                  Music
                </h3>
                <svg
                  className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                    isMusicExpanded ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isMusicExpanded && (
                <div className="h-96 border-t border-gray-200">
                  <MusicPanel
                    songs={songs}
                    playlists={playlists}
                    currentSongId={currentSongId}
                    isPlaying={isUploadedAudioPlaying}
                    isPaused={isUploadedAudioPaused}
                    onPlaySong={handlePlaySong}
                    onStopSong={handleStopSong}
                    onGoToLibraryManager={() => setActiveTab('music')}
                    onAddToQueue={handleAddToQueue}
                    onClearQueue={handleClearQueue}
                    onShuffleChange={handleShuffleChange}
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* User Section at Bottom */}
          {user && (
            <div className="flex-none border-t border-gray-200 bg-gray-50">
              <div className="p-4">
                <AuthPanel onAuthStateChange={handleAuthStateChange} />
              </div>
              <div className="px-4 pb-4">
                <button
                  onClick={() => setShowUserSettings(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Account Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <main className={`flex-1 flex flex-col w-full overflow-y-auto ${isVisualizerFullBrowser ? '' : 'max-w-5xl mx-auto'}`}>
        
        {activeTab === 'interference' ? (
          <>
            {/* Visualizer Section (Resizable, Flow layout) */}
            <div className={`flex-none animate-fade-in z-20 ${isVisualizerFullBrowser ? 'h-full' : 'p-4 sm:px-6 pb-2'}`}>
                <MasterOutput 
                  waves={waves} 
                  speed={speed} 
                  onSpeedChange={setSpeed}
                  onTogglePanels={handleTogglePanels}
                  uploadedAudioData={uploadedAudioData}
                  waveformData={waveformData}
                  isUploadedAudioPlaying={isUploadedAudioPlaying}
                  currentSong={currentSongId ? songs.find(s => s.id === currentSongId) : null}
                  showRawFrequencies={showRawFrequencies}
                  onShowRawFrequenciesChange={setShowRawFrequencies}
                />
            </div>
          </>
        ) : activeTab === 'fourier' ? (
          <div className="flex-1 overflow-y-auto p-4 sm:px-6">
             <FourierLab
               ref={fourierLabRef}
               onExportToLab={handleImportFromFourier} 
               importedWaves={importedWavesToFourier}
               onImportProcessed={() => setImportedWavesToFourier(null)}
             />
          </div>
        ) : activeTab === 'music' ? (
          <div className="flex-1 overflow-hidden">
            <MusicLibraryMode
              songs={songs}
              playlists={playlists}
              currentSongId={currentSongId}
              isPlaying={isUploadedAudioPlaying}
              isPaused={isUploadedAudioPaused}
              onPlaySong={handlePlaySong}
              onStopSong={handleStopSong}
              onDeleteSong={handleDeleteSong}
              onCreatePlaylist={handleCreatePlaylist}
              onDeletePlaylist={handleDeletePlaylist}
              onAddToPlaylist={handleAddToPlaylist}
              onRemoveFromPlaylist={handleRemoveFromPlaylist}
              onOpenUploader={() => setShowAudioUploader(true)}
              onAddToQueue={handleAddToQueue}
              onClearQueue={handleClearQueue}
              onShuffleChange={handleShuffleChange}
              onUpdateSongMetadata={handleUpdateSongMetadata}
            />
          </div>
        ) : activeTab === 'wavepool' ? (
          <div className="relative flex-1 overflow-y-auto bg-gray-50/50 text-gray-800">
            <div className="flex-1 overflow-auto p-6 sm:p-10">
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono tracking-widest text-gray-500 uppercase">Digital Wave Interference Pool</p>
                    <h2 className="text-3xl font-semibold text-gray-900">AMOEBA<span className="text-blue-600">.sim</span></h2>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">Grid 200x200 · FPS 60</div>
                </div>

                {/* AI Wave Designer Section */}
                <div className="bg-white dark:bg-slate-800 dark:border-slate-700 rounded-xl shadow-md border border-gray-300 p-6">
                  <AIPanel onAIResponse={handleAIWaveResponse} />
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl shadow-md p-8 sm:p-16 relative">
                  {wavePoolFocusPoints && wavePoolFocusPoints.length > 0 && (
                    <div className="absolute top-4 left-4 sm:top-6 sm:left-6 pointer-events-none">
                      <span className="text-[10px] text-[#64ffda] bg-[#0a192f] px-3 py-1 rounded-full border border-[#64ffda]/30 tracking-widest uppercase animate-pulse inline-block">
                        Phased Array Active
                      </span>
                    </div>
                  )}
                  
                  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 pointer-events-none">
                    <span className="text-xs text-[#64ffda] bg-black/50 px-2 py-1 rounded font-mono inline-block">
                      INTERACTIVE
                    </span>
                  </div>

                  <WavePool
                    config={wavePoolConfig}
                    emitters={wavePoolEmitters}
                    focusPoints={wavePoolFocusPoints}
                    resetTrigger={wavePoolResetTrigger}
                  />
                  <div className="mt-6 text-center space-y-4">
                    {wavePoolAIResponse && wavePoolConfig.pattern === WavePattern.AI_GENERATED && (
                      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <h3 className="text-2xl font-semibold text-gray-900">{wavePoolAIResponse.name}</h3>
                        <p className="text-xs text-gray-600 mt-2">{wavePoolAIResponse.description}</p>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                      Click and drag on the pool to create manual ripples.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        </main>
      </div>
      
      {/* Right Sidebar Panel */}
      {!isVisualizerFullBrowser && (
      <aside 
        className={`flex-none transition-all duration-300 ease-in-out ${
          isPanelRightOpen ? 'w-80' : 'w-0'
        } overflow-hidden flex flex-col relative z-30 h-full bg-white border-l border-gray-200 shadow-lg`}
      >
        <div className="w-80 flex flex-col h-full overflow-hidden">
          {/* Panel Content - Contextual based on active tab */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'interference' && (
              <>
                {/* Frequency Layers Section */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => setIsLayersExpanded(!isLayersExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="h-4 w-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      Frequency Layers
                    </h3>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        isLayersExpanded ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isLayersExpanded && (
                    <div className="p-4 space-y-4">
                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => addWave()} 
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-xs font-medium shadow-sm transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Add
                          </button>
                          <button 
                            onClick={resetWaves} 
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-xs font-medium shadow-sm transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                            Reset
                          </button>
                        </div>
                        
                        {/* Merge All Frequencies Button */}
                        <button 
                          onClick={mergeAllWaves} 
                          className="w-full inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-xs font-medium shadow-sm transition-colors"
                          title="Merge all unmuted frequencies into one"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 112 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 110 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 110-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Merge All Frequencies
                        </button>
                        
                        {/* Open in Fourier Button */}
                        <button 
                          onClick={() => {
                            setImportedWavesToFourier(waves);
                            setActiveTab('fourier');
                            analyticsService.trackFeatureUse('open_fourier_from_wavelab', `waves_count:${waves.length}`);
                          }} 
                          className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl text-sm font-bold shadow-lg transition-all hover:scale-105 active:scale-95 hover:shadow-xl"
                          title="Open Fourier Lab to analyze wave composition"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span>Open in Fourier</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Wave Cards */}
                      <div className="space-y-3">
                        {waves.map((wave) => (
                          <WaveCard 
                            key={wave.id} 
                            wave={wave} 
                            onChange={updateWave} 
                            onRemove={removeWave}
                            onCounter={addCounterWave}
                            isRemovable={waves.length > 1}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Presets Section */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => setIsPresetsExpanded(!isPresetsExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                      </svg>
                      Presets
                    </h3>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        isPresetsExpanded ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isPresetsExpanded && (
                    <ConfigManager 
                      currentWaves={waves}
                      currentSpeed={speed}
                      onLoadConfig={handleLoadConfig}
                      onConfigSaved={() => console.log('Config saved!')}
                      mode="presets"
                    />
                  )}
                </div>
                
                {/* Favorites Section */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => setIsFavoritesExpanded(!isFavoritesExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="h-4 w-4 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                      Favorite Frequencies
                    </h3>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        isFavoritesExpanded ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isFavoritesExpanded && (
                    <ConfigManager 
                      currentWaves={waves}
                      currentSpeed={speed}
                      onLoadConfig={handleLoadConfig}
                      onConfigSaved={() => console.log('Config saved!')}
                      onAddFavoriteToWorkspace={handleAddFavoriteToWorkspace}
                      mode="favorites"
                    />
                  )}
                </div>

                {/* Saved Configurations Section */}
                {user && (
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setIsSavedConfigsExpanded(!isSavedConfigsExpanded)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <svg className="h-4 w-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                        </svg>
                        My Configurations
                      </h3>
                      <svg
                        className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          isSavedConfigsExpanded ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isSavedConfigsExpanded && (
                      <ConfigManager 
                        currentWaves={waves}
                        currentSpeed={speed}
                        onLoadConfig={handleLoadConfig}
                        onConfigSaved={() => console.log('Config saved!')}
                        mode="saved"
                      />
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'fourier' && fourierLabRef.current && (
              <div className="p-4">
                {fourierLabRef.current.renderControls()}
              </div>
            )}

            {activeTab === 'wavepool' && (
              <div className="space-y-4 p-4">
                {/* Controls Tab */}
                <WavePoolControls
                  config={wavePoolConfig}
                  emitters={wavePoolEmitters}
                  onConfigChange={setWavePoolConfig}
                  onPatternSelect={(pattern) => setWavePoolConfig(prev => ({ ...prev, pattern }))}
                  onReset={() => {
                    setWavePoolConfig(prev => ({ ...prev, autoPlay: false }));
                    setWavePoolResetTrigger(prev => prev + 1);
                  }}
                  onExportToLab={handleImportFromWavePool}
                />

                {/* Config Editor - Simulation Config */}
                <ConfigEditor 
                  data={{
                    name: wavePoolAIResponse?.name || 'Custom Configuration',
                    description: wavePoolAIResponse?.description || 'User-defined wave pattern',
                    config: wavePoolConfig,
                    emitters: wavePoolEmitters,
                    focusPoints: wavePoolFocusPoints
                  }}
                  onUpdate={(response) => {
                    if (response.config) {
                      setWavePoolConfig(prev => ({ ...prev, ...response.config }));
                    }
                    if (response.emitters) {
                      setWavePoolEmitters(response.emitters);
                    }
                    if (response.focusPoints !== undefined) {
                      setWavePoolFocusPoints(response.focusPoints);
                    }
                    // Update AI response state to keep it in sync
                    setWavePoolAIResponse(response);
                  }}
                />

                {/* Presets Section */}
                <div className="border border-slate-200 rounded-xl bg-white">
                  <button
                    onClick={() => setIsWavePoolPresetsExpanded(!isWavePoolPresetsExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-slate-200"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      Presets
                    </h3>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        isWavePoolPresetsExpanded ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isWavePoolPresetsExpanded && (
                    <div className="px-4 pt-3 pb-4">
                      <WavePoolConfigManager
                        currentConfig={wavePoolConfig}
                        currentEmitters={wavePoolEmitters}
                        currentFocusPoints={wavePoolFocusPoints}
                        onLoadConfig={(config, emitters, focusPoints, meta) => {
                          const normalized = normalizeWavePoolConfig(config);
                          setWavePoolConfig(normalized);
                          setWavePoolEmitters(emitters);
                          if (focusPoints !== undefined) {
                            setWavePoolFocusPoints(focusPoints);
                          }
                          setWavePoolResetTrigger(prev => prev + 1);
                          if (meta) {
                            setWavePoolAIResponse({
                              name: meta.name || 'Preset',
                              description: meta.description || '',
                              config: normalized,
                              emitters,
                              focusPoints
                            });
                          }
                        }}
                        mode="presets"
                      />
                    </div>
                  )}
                </div>

                {/* Saved Configurations Section */}
                <div className="border border-slate-200 rounded-xl bg-white">
                  <button
                    onClick={() => setIsWavePoolSavedConfigsExpanded(!isWavePoolSavedConfigsExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-slate-200"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      Saved Configurations
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowWavePoolSaveDialog(true);
                        }}
                        title="Save Current Configuration"
                        className="p-2 rounded-lg transition-colors bg-blue-100 text-blue-600 hover:bg-blue-200 text-xs"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                      </button>
                      <svg
                        className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                          isWavePoolSavedConfigsExpanded ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {isWavePoolSavedConfigsExpanded && (
                    <div className="px-4 pt-3 pb-4">
                      <WavePoolConfigManager
                        key={wavePoolConfigRefreshTrigger}
                        currentConfig={wavePoolConfig}
                        currentEmitters={wavePoolEmitters}
                        currentFocusPoints={wavePoolFocusPoints}
                        onLoadConfig={(config, emitters, focusPoints, meta) => {
                          const normalized = normalizeWavePoolConfig(config);
                          setWavePoolConfig(normalized);
                          setWavePoolEmitters(emitters);
                          if (focusPoints !== undefined) {
                            setWavePoolFocusPoints(focusPoints);
                          }
                          setWavePoolResetTrigger(prev => prev + 1);
                          if (meta) {
                            setWavePoolAIResponse({
                              name: meta.name || 'Saved Config',
                              description: meta.description || '',
                              config: normalized,
                              emitters,
                              focusPoints
                            });
                          }
                        }}
                        onConfigSaved={() => {
                          setWavePoolConfigRefreshTrigger(prev => prev + 1);
                        }}
                        mode="saved"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
      )}
      </div>
      
      {/* Music Player Bar - positioned above footer */}
      {showMusicPlayer && (
      <MusicPlayer
        currentSong={songs.find(s => s.id === currentSongId) || null}
        isPlaying={isUploadedAudioPlaying}
        isPaused={isUploadedAudioPaused}
        isLoading={isLoadingSong}
        currentTime={currentPlaybackTime}
        volume={playbackVolume}
        loopMode={loopMode}
        shuffleEnabled={shuffleEnabled}
        queue={playQueue}
        onPlayPause={handlePlayPause}
        onStop={handleStopSong}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSeek={handleSeek}
        onVolumeChange={handleVolumeChange}
        onLoopModeChange={handleLoopModeChange}
        onShuffleToggle={handleShuffleToggle}
        onClose={handleCloseMusicPlayer}
        onToggleQueue={() => setShowQueuePanel(!showQueuePanel)}
      />
      )}

      {/* Queue Panel */}
      <QueuePanel
        isOpen={showQueuePanel}
        onClose={() => setShowQueuePanel(false)}
        queue={playQueue}
        currentSongId={currentSongId}
        onPlaySong={handlePlaySong}
        onRemoveFromQueue={handleRemoveFromQueue}
        onClearQueue={handleClearQueue}
      />
      
      {/* Footer - Full Width */}
      {!isVisualizerFullBrowser && (
      <footer className="flex-none py-2 text-center text-[10px] text-gray-300 bg-gray-50 border-t border-gray-100">
        <p className="flex items-center justify-center gap-3">
          <span>Reely, Inc. &copy; {new Date().getFullYear()}</span>
          <span className="text-gray-200">•</span>
          <button
            onClick={() => setShowTerms(true)}
            className="hover:text-gray-500 underline"
          >
            Terms
          </button>
          <span className="text-gray-200">•</span>
          <a 
            href="https://www.freeprivacypolicy.com/live/fdaf7fad-f80f-4146-ab48-df488331f001" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-gray-500 underline"
          >
            Privacy
          </a>
        </p>
      </footer>
      )}

      {/* Modal Components */}
      {showAnalysis && (
        <AnalysisPanel 
          waves={waves}
          isVisible={showAnalysis}
          onClose={() => setShowAnalysis(false)}
        />
      )}
      
      {showAudioProcessor && (
        <AudioProcessor
          waves={waves}
          isVisible={showAudioProcessor}
          onClose={() => setShowAudioProcessor(false)}
        />
      )}
      
      {showAudioUploader && (
        <AudioUploader
          isVisible={showAudioUploader}
          onClose={() => setShowAudioUploader(false)}
          onSongUploaded={handleSongUploaded}
          existingSongs={songs}
        />
      )}
      
      {showTerms && (
        <TermsOfUse onClose={() => setShowTerms(false)} />
      )}
      
      {/* User Settings Modal */}
      <UserSettingsModal
        isOpen={showUserSettings}
        onClose={() => setShowUserSettings(false)}
        user={user}
        profile={userProfile}
      />
      
      {/* Save Dialog */}
      {showSaveDialog && (
        <ConfigManager 
          currentWaves={waves}
          currentSpeed={speed}
          onLoadConfig={handleLoadConfig}
          onConfigSaved={() => {
            console.log('Config saved!');
            setShowSaveDialog(false);
          }}
          mode="save-dialog"
          onClose={() => setShowSaveDialog(false)}
        />
      )}

      {/* Wave Pool Save Dialog */}
      {showWavePoolSaveDialog && (
        <WavePoolConfigManager 
          currentConfig={wavePoolConfig}
          currentEmitters={wavePoolEmitters}
          currentFocusPoints={wavePoolFocusPoints}
          defaultName={wavePoolAIResponse?.name || 'Wave Pool Configuration'}
          defaultDescription={wavePoolAIResponse?.description || ''}
          onLoadConfig={(config, emitters, focusPoints, meta) => {
            const normalized = normalizeWavePoolConfig(config);
            setWavePoolConfig(normalized);
            setWavePoolEmitters(emitters);
            if (focusPoints !== undefined) {
              setWavePoolFocusPoints(focusPoints);
            }
            setWavePoolResetTrigger(prev => prev + 1);
            if (meta) {
              setWavePoolAIResponse({
                name: meta.name || 'Saved Config',
                description: meta.description || '',
                config: normalized,
                emitters,
                focusPoints
              });
            }
          }}
          onConfigSaved={() => {
            console.log('Wave Pool Config saved!');
            setShowWavePoolSaveDialog(false);
            setWavePoolConfigRefreshTrigger(prev => prev + 1);
          }}
          mode="save-dialog"
          onClose={() => setShowWavePoolSaveDialog(false)}
        />
      )}
    </div>
    </ThemeProvider>
  );
}

export default App;